/**
 * **한국어 조사 붙이기.**
 *
 * 문구표에 `{what}를 열까요?` 라고 박아 두면 받침 있는 말이 들어올 때 어긋난다
 * ("전체 리포트를" 은 맞지만 "질문 타로 1회를" 은… 맞고, "행운 배경화면를" 은 틀리다).
 * 그래서 **낱말에 조사를 붙여서** 문구표에 넣는다.
 *
 * 한국어가 아닌 언어는 그대로 돌려준다 — 조사가 없다.
 */

/** 마지막 글자에 받침이 있는가. 한글이 아니면 null (판단하지 않는다). */
function hasFinalConsonant(word: string): boolean | null {
  const trimmed = word.trim()
  const last = trimmed.at(-1)
  if (!last) return null
  const code = last.codePointAt(0)!
  // 가(0xAC00) ~ 힣(0xD7A3)
  if (code < 0xac00 || code > 0xd7a3) return null
  return (code - 0xac00) % 28 !== 0
}

/**
 * 목적어 조사 (을/를). 한국어가 아니거나 한글로 끝나지 않으면 조사를 붙이지 않는다.
 *
 *   withObjectParticle('ko', '전체 리포트') → '전체 리포트를'
 *   withObjectParticle('ko', '행운 배경화면') → '행운 배경화면을'
 *   withObjectParticle('en', 'the full report') → 'the full report'
 */
export function withObjectParticle(locale: string, word: string): string {
  if (locale !== 'ko') return word
  const has = hasFinalConsonant(word)
  if (has === null) return word
  return `${word}${has ? '을' : '를'}`
}

/*
 * **문구표의 조사 표시를 받침에 맞게 고른다** (26-10-09).
 *
 * 빈칸 뒤 조사는 들어올 낱말을 모르니 예전에는 띄어 썼다 ("{date} 에", "「{letters}」 가"). 한국어 화면에
 * "9일 에 본 기록", "「김」 가 이미" 처럼 나왔다. 이제 문구표에 우리말 표기 그대로 "(이)가 (은)는 (을)를
 * (과)와 (으)로" 를 적고, 빈칸을 채운 뒤 앞 글자의 받침으로 하나를 고른다. 앞 글자는 닫는 낫표·따옴표를
 * 건너뛰고 본다. 숫자는 읽는 소리(일·삼·육·칠·팔·영)로 본다. 한글·숫자가 아니면(영어 이름 등) 받침 없는 쪽.
 */
// 끝의 물음표·느낌표·마침표·말줄임·빈칸도 건너뛴다 — 「그 사람 속마음은?」를 → 을
const CLOSERS = '」』"\'’”)]›»?!.…？！。~ '
/** 숫자를 읽었을 때 받침 — 0 영(ㅇ) 1 일(ㄹ) 3 삼(ㅁ) 6 육(ㄱ) 7 칠(ㄹ) 8 팔(ㄹ) */
const DIGIT_FINAL: Record<string, 'none' | 'rieul' | 'other'> = { '0': 'other', '1': 'rieul', '2': 'none', '3': 'other', '4': 'none', '5': 'none', '6': 'other', '7': 'rieul', '8': 'rieul', '9': 'none' }

function finalOf(ch: string | undefined): 'none' | 'rieul' | 'other' | null {
  if (!ch) return null
  // 자주 인용되는 한자의 한국식 읽기. 알 수 없는 한자는 추측하지 않는다.
  const hanjaReading: Record<string, string> = { 明: '명', 木: '목' }
  if (hanjaReading[ch]) return finalOf(hanjaReading[ch])
  if (ch in DIGIT_FINAL) return DIGIT_FINAL[ch]!
  if (ch === 'ㄹ') return 'rieul'
  if (/[ㄱ-ㅎ]/u.test(ch)) return 'other'
  const code = ch.codePointAt(0)!
  if (code < 0xac00 || code > 0xd7a3) return null
  const jong = (code - 0xac00) % 28
  return jong === 0 ? 'none' : jong === 8 ? 'rieul' : 'other'
}

const MARK = /\((이|은|을|과|으)\)(가|는|를|와|로)/g
/**
 * **서술격 조사 "(이)…"** 도 같은 함수로 — "(이)라고 (이)야 (이)에요 (이)랑 (이)나 (이)면 (이)든" 등.
 * 공유 문구에 "누런 용라고", "흰 닭를" 처럼 나왔다 (받침을 보지 않고 한 꼴로 적었다).
 * "(이)에요" 는 받침이 없으면 "예요" 가 된다 ("용이에요" · "말이에요" · "소예요").
 */
// "(이)다"·"(이)죠" 를 더했다 (궁합 글 "서윤다 보니")
const COPULA = /\(이\)(라고|라는|라서|라면|야|에요|예요|랑|나|면|든|지만|니까|었어요|었다|었음|었|고|며|다|죠)/g

/** 빈칸을 채운 글에서 앞 글자(닫는 표시·물음표는 건너뜀)의 받침 */
function finalBefore(text: string, offset: number): 'none' | 'rieul' | 'other' {
  let i = offset - 1
  while (i >= 0 && CLOSERS.includes(text[i]!)) i -= 1
  // 한글·숫자가 아니면(영어 이름·주소) 받침 없는 쪽으로 — 화면에 괄호 조사를 남기지 않는다
  return finalOf(text[i]) ?? 'none'
}

export function resolveParticleMarks(text: string): string {
  if (!text.includes(')')) return text
  return text
    .replace(MARK, (_whole, withFinal: string, without: string, offset: number) => {
      const fin = finalBefore(text, offset)
      if (withFinal === '으') return fin === 'other' ? '으로' : '로'
      return fin === 'none' ? without : withFinal
    })
    .replace(COPULA, (_whole, tail: string, offset: number, all: string) => {
      const fin = finalBefore(all, offset)
      if (tail === '에요' || tail === '예요') return fin === 'none' ? '예요' : '이에요'
      if (tail.startsWith('었')) return fin === 'none' ? `였${tail.slice(1)}` : `이${tail}`
      return fin === 'none' ? tail : `이${tail}`
    })
}

/**
 * 낱말 하나에 조사를 붙인다 — 문구표가 아니라 코드에서 이어 붙일 때 (공유 문구·이름 등).
 *   josa('용', '(이)라고') → '용이라고' · josa('닭', '(을)를') → '닭을' · josa('말', '(으)로') → '말로'
 */
export function josa(word: string, mark: string): string {
  return resolveParticleMarks(`${word}${mark}`)
}

/** 짝이 있는 조사 — 앞 글자에 받침이 있을 때 / 없을 때 */
const PAIRS: Record<string, [string, string]> = { 이: ['이', '가'], 가: ['이', '가'], 은: ['은', '는'], 는: ['은', '는'], 을: ['을', '를'], 를: ['을', '를'], 과: ['과', '와'], 와: ['과', '와'] }

/**
 * **AI 글에서 닫는 낫표 뒤 띄운 조사를 붙인다**. 이름 풀이 글이 "「김」 이 품은",
 * "「바」 와 「다」 는" 처럼 나왔다 — 글 쓰는 쪽에 준 재료가 그렇게 띄어 있었다(지금은 고쳤다). 이미 저장된
 * 글도 있어 화면에서 고친다. 낫표 바로 뒤 조사는 다른 낱말일 수 없어 안전하다. 짝 조사는 낫표 안 마지막
 * 글자의 받침에 맞춘다(한글·숫자가 아니면 그대로 둔다).
 */
export function tightenBracketParticles(text: string): string {
  const quoted = text.replace(/([^\s」])」\s*(이|가|은|는|을|를|과|와|으로|로|에서|에게|에|의|도|만)(?=[\s.,!?·)]|$)/g, (_whole, prev: string, particle: string) => {
    const fin = finalOf(prev)
    let p = particle
    if (PAIRS[particle] && fin !== null) p = PAIRS[particle]![fin === 'none' ? 1 : 0]
    else if ((particle === '으로' || particle === '로') && fin !== null) p = fin === 'other' ? '으로' : '로'
    return `${prev}」${p}`
  })
  return quoted.replace(/(^|\s)(펜타클|컵|완드|소드)\s+(\d{1,2})(이|가)(?=[\s.,!?·)]|$)/g,
    (_whole, lead: string, suit: string, number: string) => lead + josa(`${suit} ${number}`, '(이)가'))
}
